import React, { useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import {
  Box,
  Button,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { GridContainer } from "../../../../components";
import moment from "moment";
import { getInstrumentsByIds } from "../../../../api/Instruments";
import type { Instrument } from "../../../../types";
import "./AIPreviewTable.css";

/** Generic CSV row stored on trade.importJson includes the raw Instrument cell */
function instrumentLabelFromImportJson(trade: any): string | undefined {
  try {
    const raw = trade?.importJson;
    if (!raw) return undefined;
    const j = typeof raw === "string" ? JSON.parse(raw) : raw;
    const v = j?.Instrument;
    return typeof v === "string" && v.trim() ? v.trim() : undefined;
  } catch {
    return undefined;
  }
}

const TRADE_TYPES = [
  "Equity",
  "Option",
  "Future",
  "Forex",
  "Fund",
  "Bond",
  "Cash",
  "Unknown",
];
const BUY_SELL = ["Buy", "Sell"];
const CURRENCIES = ["USD", "EUR", "GBP", "AUD", "CAD", "CHF", "JPY"];

type Props = {
  trades: any[];
  cashflows: any[];
  /** Used to resolve instrument symbol/name via API */
  accountId?: number;
  readOnly?: boolean;
  onRemoveRow: (index: number) => void;
  onCellChange: (index: number, field: string, value: unknown) => void;
};

export default function AIPreviewTable({
  trades,
  cashflows,
  accountId,
  readOnly = false,
  onRemoveRow,
  onCellChange,
}: Props) {
  const [instrumentById, setInstrumentById] = useState<
    Record<number, Pick<Instrument, "symbol" | "name">>
  >({});

  const instrumentIdKey = useMemo(() => {
    const ids = new Set<number>();
    for (const t of trades) {
      if (t?.instrumentId != null) ids.add(Number(t.instrumentId));
      if (t?.underlyingInstrumentId != null)
        ids.add(Number(t.underlyingInstrumentId));
    }
    return [...ids].sort((a, b) => a - b).join(",");
  }, [trades]);

  useEffect(() => {
    if (!accountId || !instrumentIdKey) {
      return;
    }
    const ids = instrumentIdKey.split(",").map(Number).filter(Boolean);
    if (ids.length === 0) return;

    let cancelled = false;
    (async () => {
      const list = await getInstrumentsByIds(accountId, ids);
      if (cancelled || !list?.length) return;
      const next: Record<number, Pick<Instrument, "symbol" | "name">> = {};
      for (const ins of list) {
        if (ins?.id != null) {
          next[ins.id] = { symbol: ins.symbol, name: ins.name };
        }
      }
      setInstrumentById((prev) => ({ ...prev, ...next }));
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId, instrumentIdKey]);

  const gridComponents = useMemo(
    () => ({
      deleteButton: (p: ICellRendererParams) =>
        !readOnly ? (
          <Button
            size="xs"
            colorScheme="red"
            variant="outline"
            onClick={() => onRemoveRow((p.data as any)._idx)}
          >
            Delete
          </Button>
        ) : null,
    }),
    [readOnly, onRemoveRow]
  );

  const rowData = useMemo(
    () =>
      trades.map((t, i) => {
        const id = t.instrumentId != null ? Number(t.instrumentId) : undefined;
        const lookedUp = id != null ? instrumentById[id] : undefined;
        const fromImport = instrumentLabelFromImportJson(t);
        const symbol =
          lookedUp?.symbol?.trim() ||
          fromImport ||
          (id != null ? `ID ${id}` : "—");
        const name = lookedUp?.name?.trim() || "";
        const uid =
          t.underlyingInstrumentId != null
            ? Number(t.underlyingInstrumentId)
            : undefined;
        const uLookedUp =
          uid != null ? instrumentById[uid] : undefined;
        const underlyingSymbol =
          uLookedUp?.symbol?.trim() || "—";
        const underlyingName = uLookedUp?.name?.trim() || "";
        return {
          ...t,
          _idx: i,
          tradeDateEdit: t.tradeDate
            ? moment(t.tradeDate).format("YYYY-MM-DD")
            : "",
          _instrumentSymbol: symbol,
          _instrumentName: name,
          _underlyingSymbol: underlyingSymbol,
          _underlyingName: underlyingName,
        };
      }),
    [trades, instrumentById]
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: "_idx",
        headerName: "#",
        width: 56,
        editable: false,
        valueFormatter: (p) => String((p.value ?? 0) + 1),
      },
      {
        field: "_instrumentSymbol",
        headerName: "Symbol",
        width: 200,
        minWidth: 120,
        editable: false,
        tooltipValueGetter: (p) => {
          const sym = p.data?._instrumentSymbol ?? "";
          const nm = p.data?._instrumentName;
          return nm ? `${sym} — ${nm}` : sym;
        },
      },
      {
        field: "_underlyingSymbol",
        headerName: "Underlying",
        width: 160,
        minWidth: 100,
        editable: false,
        tooltipValueGetter: (p) => {
          const sym = p.data?._underlyingSymbol ?? "";
          const nm = p.data?._underlyingName;
          if (!sym || sym === "—") return "";
          return nm ? `${sym} — ${nm}` : sym;
        },
      },
      {
        field: "brokerTradeId",
        headerName: "Broker trade ID",
        width: 150,
        minWidth: 100,
        editable: !readOnly,
        valueFormatter: (p) =>
          p.value != null && String(p.value).trim() !== ""
            ? String(p.value)
            : "—",
      },
      {
        field: "tradeType",
        headerName: "Type",
        width: 110,
        editable: !readOnly,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: TRADE_TYPES },
      },
      {
        field: "tradeDateEdit",
        headerName: "Trade date",
        width: 130,
        editable: !readOnly,
      },
      {
        field: "buySell",
        headerName: "Side",
        width: 90,
        editable: !readOnly,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: BUY_SELL },
      },
      {
        field: "price",
        headerName: "Price",
        width: 90,
        editable: !readOnly,
        valueParser: (p) => parseFloat(p.newValue) || 0,
      },
      {
        field: "quantity",
        headerName: "Qty",
        width: 90,
        editable: !readOnly,
        valueParser: (p) => parseFloat(p.newValue) || 0,
      },
      {
        field: "fees",
        headerName: "Fees",
        width: 80,
        editable: !readOnly,
        valueParser: (p) => parseFloat(p.newValue) || 0,
      },
      {
        field: "currency",
        headerName: "Ccy",
        width: 80,
        editable: !readOnly,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: CURRENCIES },
      },
      {
        field: "book",
        headerName: "Book",
        width: 100,
        editable: !readOnly,
      },
      {
        field: "notes",
        headerName: "Notes",
        flex: 1,
        minWidth: 120,
        editable: !readOnly,
      },
      ...(readOnly
        ? []
        : [
            {
              field: "_actions",
              headerName: "",
              width: 100,
              editable: false,
              cellRenderer: "deleteButton",
            } as ColDef,
          ]),
    ],
    [readOnly]
  );

  return (
    <Box w="100%">
      <GridContainer customClass="aiPreviewGrid">
        <div className="ag-theme-quartz-dark" style={{ width: "100%", height: 420 }}>
          <AgGridReact
            components={gridComponents}
            rowHeight={36}
            headerHeight={36}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
            suppressCellFocus={readOnly}
            onCellValueChanged={(e) => {
              if (readOnly) return;
              const idx = e.data._idx;
              const field = e.colDef.field;
              if (!field || field === "_actions" || field.startsWith("_"))
                return;
              let val = e.newValue;
              if (field === "tradeDateEdit") {
                onCellChange(idx, "tradeDate", val);
                return;
              }
              onCellChange(idx, field, val);
            }}
          />
        </div>
      </GridContainer>

      {cashflows.length > 0 && (
        <Box mt={6} w="100%">
          <Text fontSize="sm" fontWeight="semibold" color="gray.300" mb={2}>
            Cash flows
          </Text>
          <Box overflowX="auto" borderWidth="1px" borderRadius="md" borderColor="whiteAlpha.200">
            <Table size="sm" variant="simple" sx={{ th: { color: "gray.400" } }}>
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Type</Th>
                  <Th isNumeric>Amount</Th>
                  <Th>Ccy</Th>
                  <Th>Description</Th>
                </Tr>
              </Thead>
              <Tbody>
                {cashflows.map((cf: any, i: number) => (
                  <Tr key={i}>
                    <Td>
                      {cf?.date
                        ? moment(cf.date).format("YYYY-MM-DD")
                        : "—"}
                    </Td>
                    <Td>{cf?.type ?? "—"}</Td>
                    <Td isNumeric>{cf?.value ?? "—"}</Td>
                    <Td>{cf?.currency ?? "—"}</Td>
                    <Td maxW="280px" whiteSpace="normal" fontSize="xs">
                      {cf?.description ?? "—"}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}
    </Box>
  );
}
